import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Modal from 'common/components/modal/Modal';
import Button from 'common/components/button/Button';
import InputSearch from 'common/components/inputSearch/InputSearch';
import CheckBox from 'common/components/checkBox/CheckBox';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import VehiclesService from 'api/vehicles/VehiclesService';
import { VehicleFuelCatalogItem } from 'api/vehicles/models/VehicleFuelCatalogItem';
import VehicleExpensePlafondsService from 'api/vehicleExpensePlafonds/VehicleExpensePlafondsService';
import styles from './ReplicatePlafondsModal.module.scss';

type Props = {
    isVisible: boolean;
    sourceVehicleId: string;
    onClose: () => void;
    /** Disparado depois da replicação, para quem mostra consumo vs plafond se atualizar. */
    onReplicated?: () => void;
};

/**
 * Task #66600 — copia os plafonds deste ativo para os escolhidos.
 *
 * A cópia é um snapshot: cada destino fica com plafonds próprios, pelo que editar um depois não
 * afeta os restantes. Substitui a configuração do destino — daí o aviso.
 */
const ReplicatePlafondsModal: React.FC<Props> = ({ isVisible, sourceVehicleId, onClose, onReplicated }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const [vehicles, setVehicles] = useState<VehicleFuelCatalogItem[]>([]);
    const [selected, setSelected] = useState<string[]>([]);
    const [search, setSearch] = useState<string>('');

    useEffect(() => {
        if (!isVisible) { return; }
        setSelected([]);
        setSearch('');
        loadVehicles();
    }, [isVisible]);

    const loadVehicles = async () => {
        try {
            const result = await VehiclesService.catalogWithFuel();
            // A origem nunca pode ser destino de si própria.
            setVehicles((result || []).filter(x => x.value !== sourceVehicleId));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t load the vehicles catalog', error);
            addToast(t('common.messages.error_load_info') as string, { appearance: 'error' });
        }
    };

    const toggle = (vehicleId: string) => {
        setSelected(prev => (prev.includes(vehicleId) ? prev.filter(x => x !== vehicleId) : [...prev, vehicleId]));
    };

    const onReplicate = async () => {
        if (!selected.length) { return; }
        try {
            Loading.show();
            await VehicleExpensePlafondsService.replicate({ sourceVehicleId, targetVehicleIds: selected });
            addToast(t('vehicle.plafonds.replicate_success') as string, { appearance: 'success' });
            onReplicated && onReplicated();
            onClose();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t replicate the vehicle expense plafonds', error);
            addToast(t('vehicle.plafonds.replicate_error') as string, { appearance: 'error' });
        } finally {
            Loading.hide();
        }
    };

    const term = search.trim().toLowerCase();
    const visibleVehicles = term
        ? vehicles.filter(x => `${x.label} ${x.vehicleBrandName || ''} ${x.vehicleModelName || ''}`.toLowerCase().includes(term))
        : vehicles;

    return (
        <Modal
            isOpen={isVisible}
            onRequestClose={onClose}
            shouldCloseOnOverlayClick={true}
            ariaHideApp={false}
            style={{
                content: { position: 'relative', inset: 'unset', width: 640, maxWidth: '92vw', padding: 0, overflow: 'hidden' },
            }}
        >
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h3 className={styles.title}>{t('vehicle.plafonds.replicate_title') as string}</h3>
                    <p className={styles.subtitle}>{t('vehicle.plafonds.replicate_subtitle') as string}</p>
                </div>

                <div className={styles.body}>
                    <div className={styles.warning}>{t('vehicle.plafonds.replicate_overwrite_warning') as string}</div>

                    <InputSearch
                        defaultValue={search}
                        onChangeValue={value => setSearch(value)}
                        placeholder={t('common.search') as string}
                    />

                    <div className={styles.list}>
                        {visibleVehicles.length === 0 && <div className={styles.noInfoText}>
                            {t('common.no_results') as string}
                        </div>}

                        {visibleVehicles.map(vehicle => (
                            <div key={vehicle.value} className={styles.row} onClick={() => toggle(vehicle.value)}>
                                <CheckBox
                                    checked={selected.includes(vehicle.value)}
                                    onChange={() => toggle(vehicle.value)}
                                />
                                <div className={styles.rowMain}>
                                    <div className={styles.rowName}>{vehicle.label}</div>
                                    <div className={styles.rowInfo}>
                                        {[vehicle.vehicleBrandName, vehicle.vehicleModelName].filter(x => !!x).join(' ')}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className={styles.footer}>
                    <div className={styles.selectedCount}>
                        {t('vehicle.plafonds.replicate_selected', { count: selected.length }) as string}
                    </div>
                    <div className={styles.actions}>
                        <Button preset={'secondary'} type="button" text={t('common.cancel') as string} onClick={onClose} />
                        <Button
                            type="button"
                            text={t('vehicle.plafonds.replicate_confirm') as string}
                            isDisabled={selected.length === 0}
                            onClick={onReplicate}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ReplicatePlafondsModal;
